import * as React from 'react';
import { AppContext } from '../../../App';
import { Tooltip, IconButton, CircularProgress } from '@mui/material';
import useGlynt from '../../hooks/useGlynt';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import errorResponses from '../../../errorResponses';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import { saveAs } from 'file-saver';

export interface IPackageDownloadProps {
  packagerId: string;
  packageId: string;
}

const PackageDownload: React.FC<IPackageDownloadProps> = ({ packagerId, packageId }) => {
  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const client = useQueryClient();
  const theme = useTheme();

  const downloadPackageMutation = useMutation({
    mutationFn: async () => {
      try {
        const apiClient = (await glynt).packages.wrapper.client;

        // Use fetch directly to avoid the wrapper's response interceptor
        // If we use the wrapper, it will have an issue handling the binary data
        const token = apiClient.defaults.headers.common.Authorization;
        const baseURL = apiClient.defaults.baseURL;
        const dataPoolId = (await glynt).packages.wrapper.dataPoolId;

        const response = await fetch(
          `${baseURL}data-pools/${dataPoolId}/packagers/${packagerId}/download-package`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token as string,
            },
            body: JSON.stringify({ package_id: packageId }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();

        saveAs(blob, `package-${packageId}.zip`);

        return blob;
      } catch (error) {
        console.error('Download error:', error);
        throw error;
      }
    },

    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['packages']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Package downloaded successfully.',
      });
    },

    onError: (error) => {
      console.error('Download mutation error:', error);
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDownloadPackage,
      });
    }
  });

  const handleDownload = () => {
    downloadPackageMutation.mutate();
  };

  return (
    <>
      <Tooltip placement="top" title={`Download Package`} disableFocusListener disableTouchListener>
        <IconButton
          data-cy={`btn-download-package-${packageId}`}
          onClick={!downloadPackageMutation.isPending ? handleDownload : undefined}
          disabled={downloadPackageMutation.isPending}
          sx={{
            color: theme.palette.custom.lightGreen,
            '&:hover': {
              backgroundColor: theme.palette.custom.lightGreen + '20', // 20% opacity for hover
            },
          }}
        >
          {downloadPackageMutation.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <DownloadIcon fontSize="medium" />
          )}
        </IconButton>
      </Tooltip>
    </>
  );
};

export default PackageDownload;
